import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    X,
    AlertTriangle,
    FileText,
    Send,
    CheckCircle2,
    Video,
    Clock,
    Hash,
    User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { useTickets } from '../../contexts/TicketContext';
import { formatPunishmentDuration } from '../../utils/punishmentUtils';

const PunishmentAppealModal = ({ punishment, isOpen, onClose }) => {
    const { user } = useAuth();
    const { createAppeal, canAppeal } = usePunishment();
    const { createTicket } = useTickets();
    const [appealText, setAppealText] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [violationTime, setViolationTime] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!appealText.trim()) {
            alert('Por favor, escreva sua contestação');
            return;
        }

        if (appealText.length > 1000) {
            alert('A contestação não pode ter mais de 1000 caracteres');
            return;
        }

        setIsSubmitting(true);

        try {
            // Create appeal ticket
            const ticketData = {
                title: `Recurso de Punição - ${punishment.characterName || 'Global'}`,
                description: [
                    `[vermelho:🚨 Recurso de Punição]`,
                    ``,
                    `**👤 Alvo:** ${punishment.characterName || user?.name || 'Usuário Global'}`,
                    `**🆔 ID:** ${punishment.characterId || user?.id || 'N/A'}`,
                    `**🆔 ID da Punição:** ${punishment.id}`,
                    `**📅 Data da Aplicação:** ${new Date(punishment.appliedAt).toLocaleDateString('pt-BR')}`,
                    `**🚫 Tipo:** ${punishment.punishmentType}`,
                    ``,
                    `[azul:📝 Contestação do Usuário]`,
                    `${appealText}`,
                    ``,
                    `[laranja:📹 Provas / Detalhes Adicionais]`,
                    `**Vídeo:** ${videoUrl || 'Nenhum vídeo anexado'}`,
                    `**Minuto/Momento:** ${violationTime || 'Não informado'}`,
                    ``,
                    `---`,
                    `*Este ticket foi gerado automaticamente pelo sistema de recursos.*`
                ].join('\n'),
                category: 'Recurso',
                priority: 'high',
                type: 'APPEAL',
                metadata: {
                    punishmentId: punishment.id,
                    originalPunishment: punishment
                }
            };

            const response = await createTicket(ticketData);

            if (response.success) {
                // Update punishment with appeal
                createAppeal(punishment.id, appealText, response.ticket.id);
            } else {
                console.error('Failed to create ticket:', response.error);
                alert('Erro ao criar ticket de recurso.');
                return;
            }

            setSubmitted(true);
            setTimeout(() => {
                onClose();
                setSubmitted(false);
                setAppealText('');
                setVideoUrl('');
                setViolationTime('');
            }, 3000);
        } catch (error) {
            console.error('Error submitting appeal:', error);
            alert('Erro ao enviar recurso. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!punishment) return null;

    const canSubmitAppeal = canAppeal(punishment);

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4"
                // Removed onClick={onClose} to prevent closing on backdrop click
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        className="w-full max-w-2xl"
                    >
                        <Card className="bg-[#0f1115] border border-red-500/30 rounded-3xl overflow-hidden shadow-2xl shadow-red-900/20 ring-1 ring-red-500/10">
                            <CardHeader className="border-b border-white/5 bg-gradient-to-r from-red-500/10 via-[#0f1115] to-[#0f1115] relative p-6">
                                <Button
                                    onClick={onClose}
                                    className="absolute top-6 right-6 bg-white/5 hover:bg-white/10 hover:text-white text-gray-400 border border-white/5 rounded-full h-8 w-8 p-0 transition-all hover:rotate-90"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                                <CardTitle className="flex items-center gap-3 text-white pr-12">
                                    <div className="h-10 w-10 rounded-xl bg-red-500/20 flex items-center justify-center border border-red-500/20">
                                        <AlertTriangle className="h-5 w-5 text-red-500" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-white leading-tight">Contestar Punição</h2>
                                        <p className="text-sm text-red-400/80 font-normal mt-0.5">Recurso Administrativo</p>
                                    </div>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6 space-y-6">
                                {!submitted ? (
                                    <>
                                        {/* Character & Punishment Info Card */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-white/5 rounded-2xl p-4 border border-white/5 space-y-3">
                                                <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider">
                                                    <User className="h-3 w-3" />
                                                    {punishment.characterName ? 'Personagem' : 'Usuário'}
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-white font-bold">{punishment.characterName || user?.name || 'Usuário'}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {punishment.characterName
                                                                ? `Passaporte: ${punishment.characterId || 'N/A'}`
                                                                : `Global ID: ${user?.id || 'N/A'}`}
                                                        </p>
                                                    </div>
                                                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/20 flex items-center justify-center">
                                                        <span className="text-xs font-bold text-blue-400">ID</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-red-500/5 rounded-2xl p-4 border border-red-500/10 space-y-3">
                                                <div className="flex items-center gap-2 text-red-400/70 text-xs font-bold uppercase tracking-wider">
                                                    <AlertTriangle className="h-3 w-3" />
                                                    Punição
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-red-200 font-bold">{punishment.punishmentType}</p>
                                                        <p className="text-xs text-red-500/60">{formatPunishmentDuration(punishment)}</p>
                                                    </div>
                                                    <div className="px-2 py-1 rounded bg-red-500/20 border border-red-500/20 text-[10px] font-bold text-red-400 uppercase">
                                                        #{punishment.id}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Public Reason */}
                                        <div className="bg-black/20 rounded-xl p-4 border border-white/5">
                                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-bold">Motivo da Aplicação</p>
                                            <p className="text-gray-300 text-sm leading-relaxed italic border-l-2 border-red-500/30 pl-3">
                                                "{punishment.publicReason}"
                                            </p>
                                        </div>

                                        {/* Appeal Form */}
                                        {canSubmitAppeal ? (
                                            <form onSubmit={handleSubmit} className="space-y-5">

                                                {/* Text Area */}
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium text-gray-300 flex items-center justify-between">
                                                        <span>Sua Defesa</span>
                                                        <span className={`text-xs ${appealText.length > 900 ? 'text-red-400' : 'text-gray-500'}`}>
                                                            {appealText.length}/1000
                                                        </span>
                                                    </label>
                                                    <Textarea
                                                        value={appealText}
                                                        onChange={(e) => setAppealText(e.target.value)}
                                                        placeholder="Descreva detalhadamente os fatos. Seja claro, respeitoso e objetivo..."
                                                        className="bg-white/5 border-white/10 text-white min-h-[120px] resize-none focus:border-red-500/50 focus:ring-0 rounded-xl"
                                                        maxLength={1000}
                                                        required
                                                    />
                                                </div>

                                                {/* Optional Evidence Fields */}
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="md:col-span-2 space-y-2">
                                                        <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                                                            <Video className="h-3 w-3 text-blue-400" />
                                                            Vídeo (YouTube) <span className="text-gray-600 text-[10px] uppercase ml-auto">Opcional</span>
                                                        </label>
                                                        <Input
                                                            value={videoUrl}
                                                            onChange={(e) => setVideoUrl(e.target.value)}
                                                            placeholder="https://youtube.com/watch?v=..."
                                                            className="bg-white/5 border-white/10 text-white focus:border-blue-500/50 focus:ring-0 rounded-xl"
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                                                            <Clock className="h-3 w-3 text-orange-400" />
                                                            Minuto <span className="text-gray-600 text-[10px] uppercase ml-auto">Opcional</span>
                                                        </label>
                                                        <Input
                                                            value={violationTime}
                                                            onChange={(e) => setViolationTime(e.target.value)}
                                                            placeholder="Ex: 02:45"
                                                            className="bg-white/5 border-white/10 text-white focus:border-orange-500/50 focus:ring-0 rounded-xl"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-4 flex gap-3">
                                                    <div className="p-2 bg-blue-500/10 rounded-lg h-fit">
                                                        <FileText className="h-4 w-4 text-blue-400" />
                                                    </div>
                                                    <div className="text-xs text-blue-200/80 leading-relaxed">
                                                        <strong className="text-blue-300 block mb-1">Processo de Análise</strong>
                                                        Ao enviar este recurso, um ticket será gerado para a Alta Cúpula.
                                                        Certifique-se de que todas as provas estão anexadas corretamente.
                                                        Falsas argumentações podem agravar sua punição.
                                                    </div>
                                                </div>

                                                <div className="flex gap-3 pt-2">
                                                    <Button
                                                        type="button"
                                                        onClick={onClose}
                                                        className="flex-1 bg-white/5 hover:bg-white/10 text-white border border-white/10 h-10 rounded-xl transition-all"
                                                    >
                                                        Cancelar
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        disabled={isSubmitting || !appealText.trim()}
                                                        className="flex-[2] bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white shadow-lg shadow-red-900/20 border border-red-500/20 h-10 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {isSubmitting ? (
                                                            <div className="flex items-center gap-2">
                                                                <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                                                Enviando...
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                <Send className="h-4 w-4" />
                                                                Enviar Contestação
                                                            </div>
                                                        )}
                                                    </Button>
                                                </div>
                                            </form>
                                        ) : (
                                            <div className="bg-orange-500/5 border border-orange-500/10 rounded-2xl p-8 text-center">
                                                <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-orange-500/20">
                                                    <AlertTriangle className="h-8 w-8 text-orange-400" />
                                                </div>
                                                <h3 className="text-lg font-bold text-white mb-2">Recurso Indisponível</h3>
                                                <p className="text-orange-200/70 text-sm max-w-sm mx-auto">
                                                    {punishment.appealTicketId
                                                        ? 'Você já possui um processo de recurso em andamento para esta punição.'
                                                        : 'Esta punição não é elegível para contestação automática.'}
                                                </p>
                                                {punishment.appealTicketId && (
                                                    <Button variant="outline" className="mt-4 border-orange-500/30 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10">
                                                        Ver Andamento do Ticket
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <motion.div
                                        initial={{ scale: 0.9, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        className="text-center py-16 flex flex-col items-center justify-center"
                                    >
                                        <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6 ring-8 ring-green-500/5">
                                            <CheckCircle2 className="h-10 w-10 text-green-400" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-white mb-2">Recurso Registrado!</h3>
                                        <p className="text-gray-400 max-w-sm mx-auto mb-8">
                                            Sua contestação foi enviada com sucesso para a equipe de moderação.
                                            Você será notificado sobre o resultado.
                                        </p>
                                    </motion.div>
                                )}
                            </CardContent>
                        </Card>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PunishmentAppealModal;
